import { useState, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Skeleton } from './ui/skeleton';
import { Plus, Loader2 } from 'lucide-react';
import StoryViewer from './StoryViewer';
import CreateStory from './CreateStory';
import { postAPI } from '../services/api';
import { toast } from 'sonner';
import { getCurrentUser } from '../utils/auth';

const StoriesBar = () => {
  const [selectedStory, setSelectedStory] = useState(null);
  const [showCreateStory, setShowCreateStory] = useState(false);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewedStories, setViewedStories] = useState(() => {
    // Load viewed stories from localStorage
    const saved = localStorage.getItem('viewedStories');
    return saved ? JSON.parse(saved) : [];
  });
  const currentUser = getCurrentUser();

  const fetchStories = async () => {
    try {
      const response = await postAPI.getStories();
      console.log('Fetched stories:', response.data);
      setStories(response.data.stories || []);
    } catch (error) {
      console.error('Error fetching stories:', error);
      setStories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStories();
  }, []);

  const handleStoryCreated = (story) => {
    console.log('Story created:', story);
    fetchStories();
  };

  const handleStoryClick = (userStory) => {
    setSelectedStory(userStory);
    // Mark story as viewed
    const storyId = userStory.user.id || userStory.user._id;
    if (!viewedStories.includes(storyId)) {
      const newViewed = [...viewedStories, storyId];
      setViewedStories(newViewed);
      localStorage.setItem('viewedStories', JSON.stringify(newViewed));
    }
  };

  const isStoryViewed = (userStory) => {
    const storyId = userStory.user.id || userStory.user._id;
    return viewedStories.includes(storyId);
  };

  return (
    <>
      {/* Stories - Instagram Style without card background */}
      <div className="overflow-x-auto scrollbar-hide py-2">
        <div className="flex space-x-3 px-1">
          {/* Add Story */}
          <div 
            className="flex flex-col items-center space-y-1 min-w-[68px] flex-shrink-0 cursor-pointer group"
            onClick={() => setShowCreateStory(true)}
          >
            <div className="relative">
              <div className="p-[2px] rounded-full bg-gradient-to-tr from-primary via-purple-400 to-purple-300">
                <Avatar className="w-14 h-14 border-2 border-background">
                  <AvatarImage src={currentUser?.profileImage} alt="Your story" />
                  <AvatarFallback className="text-sm">{currentUser?.name?.[0] || 'You'}</AvatarFallback>
                </Avatar>
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-primary rounded-full flex items-center justify-center text-white border-2 border-background">
                <Plus className="w-3 h-3" />
              </div>
            </div>
            <span className="text-[11px] text-foreground text-center font-medium">Your Story</span>
          </div>

          {/* Loading skeleton circles */}
          {loading && (
            <div className="flex space-x-3" data-testid="stories-skeleton">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="flex flex-col items-center space-y-1 min-w-[68px] flex-shrink-0">
                  <Skeleton className="w-[60px] h-[60px] rounded-full" />
                  <Skeleton className="h-3 w-10" />
                </div>
              ))}
            </div>
          )}

          {/* Stories */}
          {!loading && stories.map((userStory) => {
            const viewed = isStoryViewed(userStory);
            return (
              <div
                key={userStory.user.id}
                className="flex flex-col items-center space-y-1 min-w-[68px] flex-shrink-0 cursor-pointer group"
                onClick={() => handleStoryClick(userStory)}
              >
                <div className="relative">
                  {/* Gradient ring - hidden when viewed */}
                  <div 
                    className={`p-[2px] rounded-full transition-all ${
                      viewed 
                        ? 'bg-gray-300 dark:bg-gray-600' 
                        : 'bg-gradient-to-tr from-primary via-purple-400 to-purple-300'
                    }`}
                  >
                    <Avatar className="w-14 h-14 border-2 border-background group-hover:scale-105 transition-transform">
                      <AvatarImage src={userStory.user.profileImage} alt={userStory.user.name} />
                      <AvatarFallback className="text-sm">{userStory.user.name?.[0] || 'U'}</AvatarFallback>
                    </Avatar>
                  </div>
                </div>
                <span className={`text-[11px] text-center truncate w-full font-medium ${
                  viewed ? 'text-muted-foreground' : 'text-foreground'
                }`}>
                  {userStory.user.name?.split(' ')[0] || 'User'}
                </span>
              </div>
            );
          })}

          {/* Empty state */}
          {!loading && stories.length === 0 && (
            <div className="flex items-center px-4 text-muted-foreground">
              <p className="text-xs">No stories yet</p>
            </div>
          )}
        </div>
      </div>

      {selectedStory && (
        <StoryViewer
          userStory={selectedStory}
          onClose={() => setSelectedStory(null)}
        />
      )}

      {showCreateStory && (
        <CreateStory
          onClose={() => setShowCreateStory(false)}
          onStoryCreated={handleStoryCreated}
        />
      )}
    </>
  );
};

export default StoriesBar;
